import isEmpty from 'lodash/isEmpty';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import React from 'react';
import { BorderedBanner } from 'src/components/BorderedBanner';
import { OrganizationLinkBlock } from 'src/components/OrganizationLinkBlock';
import { createApplicationError } from 'src/utils/errorFabric';

import { Banner, BANNER_TYPES } from '@sravni/react-advertisement';
import { Space } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';

import { SravniReviewsEnumsModerationReasonType } from '../../__generated__/reviews/reviewsApiTypes';
import { BackLink } from '../../components/BackLink';
import { RatingSravni } from '../../components/RatingSravni';
import { ReviewPage } from '../../components/ReviewPage';
import { SeoBlocks } from '../../components/SeoBlock';
import { SideBlock } from '../../components/SideBlock';
import { handleOrganizationRedirect } from '../../helpers/handleOrganizationRedirect';
import { getCreateReviewBlockDescription, getReviewType } from '../../helpers/reviews';
import { useIsWlPage } from '../../hooks/useIsWlPage';
import { OrganizationType } from '../../interfaces/organizations';
import { useBanners, useLoadingBanners } from '../../store/hooks/advertising';
import { useProductsTags } from '../../store/hooks/filters';
import { useOrganizationDetails } from '../../store/hooks/organizations';
import { setAdProducts } from '../../store/reducers/advertising';
import { setCommentsList } from '../../store/reducers/comments';
import { fetchSeoIfNeeded } from '../../store/reducers/metadata';
import {
  setOrganizationByAlias,
  setOrganizationPosition,
  setOrganizationRating,
} from '../../store/reducers/organizations';
import { setProductDetails } from '../../store/reducers/products';
import { setProductsRating, setRatingSravni } from '../../store/reducers/ratings';
import { setReviewItem, setReviewsList } from '../../store/reducers/reviews';
import { getUser } from '../../store/selectors/users';
import styles from '../../styles/page.module.scss';
import type { TReviewPageQuery } from '../../types/router';
import { TReviewListQuery } from '../../types/router';
import { serverDataDispatcher } from '../../utils/serverDataDispatcher';

const ReviewItemPage: NextPage = () => {
  const banners = useBanners();
  const isLoadingBanners = useLoadingBanners();
  const isMobile = useIsMobile();
  const productsTags = useProductsTags();
  const router = useRouter() as TRouter<TReviewListQuery>;
  const { query } = router;
  const { type, organization } = query;
  const orgDetails = useOrganizationDetails(organization);
  const { prepositionalName } = orgDetails;
  const isWl = useIsWlPage();

  const reviewTypes = getReviewType(type, productsTags);
  const { namesType, itemTypeUrl } = reviewTypes;

  const sideBlockDescription = getCreateReviewBlockDescription(itemTypeUrl, namesType?.mode4, prepositionalName);

  return (
    <>
      {!isMobile && <BackLink type={reviewTypes.listTypeUrl} />}
      <div className={styles.container}>
        <div className={styles.mainColumn}>
          <ReviewPage reviewTypes={reviewTypes} sideBlockDescription={sideBlockDescription} />
        </div>

        <Space className={styles.rightColumn} direction={'vertical'} size={24}>
          {orgDetails.status === 'active' && (
            <SideBlock
              title={'Оставьте отзыв'}
              description={sideBlockDescription}
              titleLevel={3}
              namesType={namesType}
              hasButton
            />
          )}
          <RatingSravni type={type} reviewTypes={reviewTypes} />
          {!isWl && (
            <div className={styles.stickyBlock}>
              {(isLoadingBanners || banners?.side) && (
                <BorderedBanner banner={banners?.side} lazy={true} type={BANNER_TYPES.SIDE} />
              )}
            </div>
          )}
        </Space>
      </div>
      <SeoBlocks />
      {!isWl && (isLoadingBanners || banners?.outOfPage) && (
        <Banner banner={banners?.outOfPage} type={BANNER_TYPES.OUT_OF_PAGE} />
      )}
      {orgDetails.ad && orgDetails.type === OrganizationType.SCHOOL && (
        <OrganizationLinkBlock
          organization={orgDetails}
          className={styles.mainColumn}
          rightSideClassName={styles.rightColumn}
        />
      )}
    </>
  );
};

ReviewItemPage.getInitialProps = async (ctx: App.ReduxNextPageContext<TReviewPageQuery>) => {
  const {
    store: { dispatch, getState },
    query,
    asPath,
  } = ctx;

  const [pathname] = asPath.split('?');

  dispatch(
    serverDataDispatcher(ctx.req, {
      __REVIEW__: setReviewItem,
      __REVIEWS__: setReviewsList,
      __COMMENTS_LIST__: setCommentsList,
      __ORGANIZATION__: setOrganizationByAlias,
      __AD_PRODUCTS__: setAdProducts,
      __ORGANIZATION_RATING__: setOrganizationRating,
      __ORGANIZATION_POSITION__: setOrganizationPosition,
      __SPECIFIC_PRODUCT__: setProductDetails,
      __PRODUCT_RATING__: setProductsRating,
      __RATING_SRAVNI__: setRatingSravni,
    }),
  );

  const organization = ctx.req?.__ORGANIZATION__;

  if (isEmpty(organization)) {
    if (!(await handleOrganizationRedirect(query.organization, ctx))) {
      throw createApplicationError(404, 'Not found');
    }
  }

  const review = ctx.req.__REVIEW__;
  const user = getUser(getState());

  const onModeration =
    review?.moderationReasons?.length &&
    !review?.moderationReasons.includes(SravniReviewsEnumsModerationReasonType.None) &&
    +user?.sub !== review?.userId &&
    !user?.role.includes('Sravni.Reviews.Service-Moderator') &&
    !user?.role.includes('Sravni.Reviews.Service-Admin');

  if (onModeration || organization?.id?.toString() !== review?.reviewObjectId) {
    throw createApplicationError(404, 'Not found');
  }

  await dispatch(fetchSeoIfNeeded(query, pathname, 'review'));
};

export default ReviewItemPage;
